import { useState } from 'react'

import { type Task, daysLeft, isDone, timeLeft } from '../../api/tasks'
import { Button } from '../../ui/Control'

/**
 * One open point in the list.
 *
 * Four things are on it and nothing else: **a box to tick**, **the sentence itself, editable where it
 * stands**, **the day it is due, settable where it stands too**, and the way into the detail. There is
 * no "edit" control opening a form somewhere else: the line is a field, the day is a field, they save
 * as soon as they are used, and Escape puts back what was there. That is the same rule the library
 * follows for a document's title.
 *
 * **The day is on the card and not only behind the detail surface.** Giving something a date is the
 * second most common thing anybody does to an open point, right after ticking it off, and a thing done
 * that often may not cost opening something first. The detail surface still has it — along with what
 * does not fit on a line — and the two are the same control, not two ways of meaning slightly
 * different things.
 *
 * **Overdue is decided here**, not by the service. A day is a day where the person is standing, so the
 * comparison belongs on the device that knows what today is — which is also why the whole ordering
 * needs no time zone at all.
 *
 * The card carries no "delete" of its own. Deleting is on the detail surface, one control away, and
 * ticking off is what the list itself offers — because ticking off is what actually happens to a task
 * ninety-nine times in a hundred, and a delete sitting beside it invites the wrong one.
 */
export interface TaskCardProps {
  readonly task: Task
  readonly open: boolean
  /** Ticks it off, or takes that back. */
  readonly onTickOff: (done: boolean) => void
  /** Saves a changed sentence. Called only when it actually changed. */
  readonly onChangeText: (text: string) => void
  /** Gives it a day, or takes the day away again with `null`. */
  readonly onChangeDue: (dueOn: string | null) => void
  /** Opens the detail surface: the state, and deleting. */
  readonly onOpen: () => void
}

export function TaskCard({
  task,
  open,
  onTickOff,
  onChangeText,
  onChangeDue,
  onOpen,
}: TaskCardProps) {
  /*
   * What is being typed, and what day is in the field — both held here so that using either costs no
   * wait.
   *
   * They are seeded from the task once and never synchronised afterwards, because the list keys this
   * card by what the service last said about it: a task changed anywhere — on the phone, in another
   * tab — arrives as a new card with the new words and the new day in it. Copying a prop into state
   * on every change would be the same thing with a render in between, and it is what the React
   * compiler rejects.
   */
  const [text, setText] = useState(task.text)
  const [due, setDue] = useState(task.dueOn ?? '')

  // What is left is worked out from the field rather than from the task, so the words beside it
  // change the moment a day is picked instead of when the answer comes back.
  const showing: Task = { ...task, dueOn: due === '' ? null : due }
  const left = daysLeft(showing)
  const overdue = !isDone(task) && left !== null && left < 0
  const today = !isDone(task) && left === 0

  const save = () => {
    const wanted = text.trim()
    if (wanted === '' || wanted === task.text) {
      // Nothing to save, or nothing left to save: a task that says nothing is not a task, so the
      // line goes back to what it was rather than being sent and refused.
      setText(task.text)
      return
    }
    onChangeText(wanted)
  }

  return (
    <li
      className={['task', isDone(task) ? 'task--done' : null]
        .filter((one) => one !== null)
        .join(' ')}
    >
      {/*
        A real checkbox, and not a button pretending to be one. It is announced as "checked" without
        anybody having to be told, it is operated with the space bar, and `--control-min` in the
        stylesheet is what makes it a finger-sized target rather than a browser default.
      */}
      <input
        type="checkbox"
        className="task__tick"
        checked={isDone(task)}
        aria-label={isDone(task) ? `Take back: ${task.text}` : `Tick off: ${task.text}`}
        onChange={(event) => {
          onTickOff(event.target.checked)
        }}
      />

      {/*
        The sentence is the field. Editing happens where the words are — no "open, change, save", and
        no second screen for a line of text.
      */}
      <input
        className="task__text"
        value={text}
        aria-label="What is to be done"
        onChange={(event) => {
          setText(event.target.value)
        }}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            // Stopped here, so that escaping out of a half-typed line puts the old one back rather
            // than closing whatever is around it.
            event.stopPropagation()
            setText(task.text)
          }
        }}
      />

      <div className={due === '' ? 'task__due task__due--none' : 'task__due'}>
        {/*
          The browser's own date field, on the card. It is a wheel on a phone, a typed date on a Mac,
          and understood by every screen reader without anything being explained to it — and clearing
          it is how a day is taken away again, which the field itself already offers.

          It carries no visible label, so its name says which task it belongs to: a list of twenty
          fields all called "Due" would be twenty fields nobody can tell apart by ear.
        */}
        <input
          type="date"
          className="task__date"
          value={due}
          aria-label={`Due date for: ${task.text}`}
          onChange={(event) => {
            const wanted = event.target.value
            setDue(wanted)
            onChangeDue(wanted === '' ? null : wanted)
          }}
        />
        {due !== '' && (
          <p
            className={[
              'task__when',
              overdue ? 'task__when--overdue' : null,
              today ? 'task__when--today' : null,
            ]
              .filter((one) => one !== null)
              .join(' ')}
          >
            {timeLeft(showing)}
          </p>
        )}
      </div>

      <Button
        tone="bare"
        className="task__more"
        icon="details"
        iconOnlyLabel={`Open: ${task.text}`}
        aria-current={open ? 'true' : undefined}
        onClick={onOpen}
      />
    </li>
  )
}
